import { message, Typography } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';
import { Moment } from 'moment';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import {
  CandidateDetail,
  JobPostingDetail,
  PaginatedResponse,
  RecruitmentProcessCollection,
  StageDetail,
} from '../types';
import MoveCandidateStageModal, {
  MoveCandidateStageFormValues,
  MoveCandidateStageModalProps,
} from './MoveCandidateStageModal';

interface PartialMoveCandidateStageModalProps
  extends Omit<MoveCandidateStageModalProps, 'helmetTitle'> {
  jobPostingId: JobPostingDetail['id'];
  candidatesIds: CandidateDetail['id'][];
  onSuccess?: (
    data: AxiosResponse<unknown, unknown> | void,
    variables: MoveCandidateStageFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const PartialMoveCandidateStageModal = ({
  jobPostingId,
  candidatesIds,
  onSuccess,
  ...props
}: PartialMoveCandidateStageModalProps) => {
  const queryClient = useQueryClient();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidatesIds[0],
          job_posting: jobPostingId,
        },
      },
    ],
    {
      enabled: candidatesIds.length === 1,
      select(data) {
        return data.results[0];
      },
    }
  );

  const candidate = useAPIQuery([`/candidates/${candidatesIds[0]}/`], {
    enabled: candidatesIds.length === 1,
  });

  const mutation = useAPIMutation(
    async (
      {
        stageId,
        admissionDate,
        salary,
        // Sankhya Form Values
        sendIntegrationAdmission,
        sankhyaJobRole,
        sankhyaCompany,
        sankhyaDepartment,
        affiliate,
        // Vixting Form Values
        unitBusiness,
        admissionType,
        vixtingLimitDate,
      }: MoveCandidateStageFormValues,
      api
    ) => {
      const validRecruitmentProcesses: RecruitmentProcessCollection[] | boolean = [];

      for (const candidateId of candidatesIds) {
        const validRecruitmentProcessesResponse = await queryClient.fetchQuery<
          PaginatedResponse<RecruitmentProcessCollection>,
          AxiosError
        >([
          '/recruitment-processes/',
          {
            params: {
              job_posting: jobPostingId,
              candidate: candidateId,
              status: 'ACTIVE',
            },
          },
        ]);

        if (validRecruitmentProcessesResponse.results.length > 0) {
          validRecruitmentProcesses.push(validRecruitmentProcessesResponse.results[0]);
        }
      }
      const stage = await queryClient.fetchQuery<StageDetail>(`/stages/${stageId}/`);

      for (const recruitmentProcess of validRecruitmentProcesses) {
        // Only try to move not hired or givenup candidates
        if (stage.type === 'HIRED') {
          await api.post(`/recruitment-processes/${recruitmentProcess.id}/hire/`, {
            admission_date: (admissionDate as Moment).format('YYYY-MM-DD'),
            salary: salary || 0,
          });

          const affiliateData = config.data?.enableVixtingAdmission
            ? { affiliate: affiliate || sankhyaCompany }
            : {};

          const unitBusinessData =
            config.data?.enableVixtingAdmission && config.data?.enableVixtingUnitBusiness
              ? { unit_business: unitBusiness }
              : {};

          if (sendIntegrationAdmission) {
            if (config.data?.enableVixtingAdmission) {
              const vixtingJobPosting = (
                await api.get('/vixting/vixting-job-postings/', {
                  params: {
                    job_posting_id: jobPostingId,
                  },
                })
              ).data.results[0];

              // If <integration>JobPosting doesn't exist, create it
              if (!vixtingJobPosting) {
                await api.post(`/vixting/vixting-job-postings/`, {
                  job_posting: jobPostingId,
                  company: sankhyaCompany,
                  job_role: sankhyaJobRole,
                  department: sankhyaDepartment,
                  ...affiliateData,
                  ...unitBusinessData,
                });
              }
              // If <integration>JobPosting exists but doesn't have all the informations, update it
              else if (
                !(
                  vixtingJobPosting?.company &&
                  vixtingJobPosting?.job_role &&
                  vixtingJobPosting?.department
                )
              ) {
                await api.patch(`/vixting/vixting-job-postings/${vixtingJobPosting?.id}/`, {
                  company: sankhyaCompany,
                  job_role: sankhyaJobRole,
                  department: sankhyaDepartment,
                  ...affiliateData,
                  ...unitBusinessData,
                });
              }

              await api.post(`/vixting/vixting-admissions/`, {
                recruitment_process: recruitmentProcess.id,
                admission_type: admissionType,
                limit_date: vixtingLimitDate?.format('YYYY-MM-DD'),
              });
            } else if (config.data?.enableSankhyaAdmission) {
              const sankhyaJobPosting = (
                await api.get('/sankhya/sankhya-job-postings/', {
                  params: {
                    job_posting_id: jobPostingId,
                  },
                })
              ).data.results[0];

              // If <integration>JobPosting doesn't exist, create it
              if (!sankhyaJobPosting) {
                await api.post(`/sankhya/sankhya-job-postings/`, {
                  job_posting: jobPostingId,
                  company: sankhyaCompany,
                  job_role: sankhyaJobRole,
                  department: sankhyaDepartment,
                  ...affiliateData,
                  ...unitBusinessData,
                });
              }
              // If <integration>JobPosting exists but doesn't have all the informations, update it
              else if (
                !(
                  sankhyaJobPosting?.company &&
                  sankhyaJobPosting?.job_role &&
                  sankhyaJobPosting?.department
                )
              ) {
                await api.patch(`/sankhya/sankhya-job-postings/${sankhyaJobPosting?.id}/`, {
                  company: sankhyaCompany,
                  job_role: sankhyaJobRole,
                  department: sankhyaDepartment,
                  ...affiliateData,
                  ...unitBusinessData,
                });
              }

              await api.post(`/sankhya/sankhya-admissions/`, {
                recruitment_process: recruitmentProcess.id,
              });
            }
          }
        } else if (stage.type === 'SELECTED') {
          await api.post(`/recruitment-processes/${recruitmentProcess.id}/select/`);
        } else if (stage.type === 'APPROVED') {
          await api.post(`/recruitment-processes/${recruitmentProcess.id}/approve/`);
        } else if (stage.type === 'CUSTOM') {
          await api.post(`/recruitment-processes/${recruitmentProcess.id}/custom/`, {
            stage: stage.id,
          });
        }
      }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/job-postings/');
        await queryClient.invalidateQueries(`/job-postings/${jobPostingId}/`);
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries('/candidate-history/');
      },
      onSuccess: async (data, variables, context) => {
        const stage = await queryClient.fetchQuery<StageDetail>(`/stages/${variables.stageId}/`);

        if (stage.type === 'HIRED') {
          message.success('Candidato(s) contratado(s).');
        } else {
          message.success('Candidato(s) movido(s).');
        }

        onSuccess && onSuccess(data, variables, context);
      },
      onError: (error, variables, context) => {
        message.error(error.message);
      },
    }
  );

  return (
    <MoveCandidateStageModal
      jobPostingId={jobPostingId}
      helmetTitle={
        candidatesIds.length > 1
          ? `Mover ${candidatesIds.length} pessoa(s)`
          : `Mover ${candidate.data?.name}`
      }
      title={
        candidatesIds.length > 1 ? (
          <Typography.Text>
            Mover <Typography.Text strong>{candidatesIds.length} pessoa(s)</Typography.Text>
          </Typography.Text>
        ) : (
          <Typography.Text>
            Mover <Typography.Text strong>{candidate.data?.name}</Typography.Text>
          </Typography.Text>
        )
      }
      loading={mutation.isLoading}
      onFinish={async (values) => {
        mutation.mutate(values);
      }}
      candidate={candidate.data}
      recruitmentProcess={recruitmentProcess.data}
      isHiredStageDisabled={
        (config.data?.enableSankhyaAdmission || config.data?.enableVixtingAdmission) &&
        candidatesIds.length > 1
      }
      {...props}
    />
  );
};

export default PartialMoveCandidateStageModal;
